import { Clock } from 'three';

import { ThreeDEnv } from './types';
import { isWebkit } from '@shared/lib/feature-detects';

// ---

const IS_WEBKIT = isWebkit();

export interface ActionInfo {
	handler: (a: Action) => boolean;
	timeToExecute: number;
	type: string;
	onDone?: () => void;
	customData?: Record<string, unknown>;
}

export interface Action extends ActionInfo {
	clock: Clock;
}

function CreateAction(params: ActionInfo): Action {
	return {
		...params,
		clock: new Clock(),
	};
}

export class AnimationScheduler {
	private env3D: ThreeDEnv;
	private actions: Action[] = [];
	private isRunning = false;

	constructor(env3D: ThreeDEnv) {
		this.env3D = env3D;
		this.animate();
	}

	animate() {
		this.isRunning = true;

		const results = this.actions.map(({ handler, onDone, ...rest }) => {
			const result = handler({ handler, onDone, ...rest });

			if (result && onDone) {
				onDone();
				rest.clock.stop();
			}

			return !result;
		});
		this.actions = this.actions.filter((_, index) => results[index]);
		this.env3D.renderer.render(this.env3D.scene, this.env3D.camera);

		if (this.actions.length === 0) {
			this.isRunning = false;
			return;
		}

		const schedule = () => this.animate();
		if (IS_WEBKIT) {
			setTimeout(() => {
				requestAnimationFrame(schedule);
			}, 33); // ~30fps on WebKit during active animations
		} else {
			requestAnimationFrame(schedule);
		}
	}

	addAction(actionParams: ActionInfo) {
		this.actions.push(CreateAction(actionParams));
		if (!this.isRunning) {
			this.animate();
		}
	}

	replaceAction(actionParams: ActionInfo) {
		let replaced = false;
		this.actions = this.actions.map(({ type, ...rest }) => {
			if (type === actionParams.type) {
				replaced = true;
				return CreateAction(actionParams);
			}
			return { type, ...rest };
		});

		if (!replaced) {
			this.actions.push(CreateAction(actionParams));
		}

		if (!this.isRunning) {
			this.animate();
		}
	}

	// Immediately remove actions of a given type (used to abort in-flight animations)
	cancelAction(typeToCancel: string) {
		const before = this.actions.length;
		this.actions = this.actions.filter(({ type }) => type !== typeToCancel);
		if (before !== this.actions.length && !this.isRunning && this.actions.length) {
			this.animate();
		}
	}
}
